import React, { useCallback } from 'react';
import { Info } from 'lucide-react';
import { TargetType, MonthlyRevenueData, MONTHS, calcKinerja, calcAverageKinerja } from './kinerja.types';

interface KinerjaTableProps {
  targetType: TargetType;
  year1: number;
  year2: number;
  monthlyData: MonthlyRevenueData[];
  isReadOnly?: boolean;
  onChange: (month: number, field: 'revenue_year1' | 'revenue_year2', value: number) => void;
}

function formatNumber(value: number): string {
  return value ? value.toLocaleString('id-ID') : '';
}

function KinerjaColorBadge({ pct }: Readonly<{ pct: number | null }>) {
  if (pct === null) return <span className="text-gray-400 text-sm">-</span>;

  let cls = '';
  const label = `${pct.toFixed(2)}%`;

  if (pct >= 12) {
    cls = 'bg-green-100 text-green-700';
  } else if (pct >= 0) {
    cls = 'bg-yellow-100 text-yellow-700';
  } else {
    cls = 'bg-red-100 text-red-700';
  }

  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{label}</span>;
}

function RevenueInput({
  value,
  targetType,
  onChange,
  readOnly,
}: Readonly<{
  value: number;
  targetType: TargetType;
  onChange: (v: number) => void;
  readOnly?: boolean;
}>) {
  const [rawValue, setRawValue] = React.useState<string>(() => (value ? formatNumber(value) : ''));

  React.useEffect(() => {
    setRawValue(value ? formatNumber(value) : '');
  }, [value, targetType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    const num = Number.parseInt(raw, 10) || 0;
    const formatted = num ? num.toLocaleString('id-ID') : '';
    setRawValue(formatted);
    onChange(num);
  };

  if (readOnly) {
    return (
      <span className="text-sm text-gray-700 dark:text-white">
        {targetType === 'rupiah' && value ? `Rp ` : ''}
        {formatNumber(value) || '-'}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {targetType === 'rupiah' && (
        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Rp</span>
      )}
      <input
        type="text"
        inputMode="numeric"
        value={rawValue}
        onChange={handleChange}
        className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
        placeholder="0"
      />
    </div>
  );
}

export default function KinerjaTable({
  targetType,
  year1,
  year2,
  monthlyData,
  isReadOnly = false,
  onChange,
}: Readonly<KinerjaTableProps>) {
  const averageKinerja = calcAverageKinerja(monthlyData);
  const totalYear1 = monthlyData.reduce((acc, d) => acc + (d.revenue_year1 ?? 0), 0);
  const totalYear2 = monthlyData.reduce((acc, d) => acc + (d.revenue_year2 ?? 0), 0);

  const handleChange = useCallback(
    (month: number, field: 'revenue_year1' | 'revenue_year2', value: number) => {
      onChange(month, field, value);
    },
    [onChange],
  );

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-2">
        <Info size={14} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <span className="text-xs text-gray-500 dark:text-gray-400">Input Omzet 2 Tahun Terakhir</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-[500px] w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-white w-28">Tahun</th>
              <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-white">Omzet {year1}</th>
              <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-white">Omzet {year2}</th>
              <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-white w-32">%Kinerja</th>
            </tr>
          </thead>
          <tbody>
            {MONTHS.map((monthName, idx) => {
              const month = idx + 1;
              const data = monthlyData.find(d => d.month === month);
              const rev1 = data?.revenue_year1 ?? 0;
              const rev2 = data?.revenue_year2 ?? 0;
              const kinerja = calcKinerja(rev1, rev2);

              return (
                <tr
                  key={month}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-700/50"
                >
                  <td className="px-4 py-2 font-medium text-gray-700 dark:text-white">{monthName}</td>
                  <td className="px-3 py-2">
                    <RevenueInput
                      value={rev1}
                      targetType={targetType}
                      onChange={v => handleChange(month, 'revenue_year1', v)}
                      readOnly={isReadOnly}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <RevenueInput
                      value={rev2}
                      targetType={targetType}
                      onChange={v => handleChange(month, 'revenue_year2', v)}
                      readOnly={isReadOnly}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <KinerjaColorBadge pct={kinerja} />
                  </td>
                </tr>
              );
            })}

            {/* Total row */}
            <tr className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600">
              <td className="px-4 py-3 font-bold text-gray-800 dark:text-white">Total</td>
              <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-white">
                {targetType === 'rupiah' && totalYear1 ? 'Rp ' : ''}
                {formatNumber(totalYear1) || '-'}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-white">
                {targetType === 'rupiah' && totalYear2 ? 'Rp ' : ''}
                {formatNumber(totalYear2) || '-'}
              </td>
              <td className="px-4 py-3" />
            </tr>

            {/* Rata-rata row */}
            <tr className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600">
              <td className="px-4 py-3 font-bold text-gray-800 dark:text-white">Rata-rata</td>
              <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-white">
                {targetType === 'rupiah' && totalYear1 ? 'Rp ' : ''}
                {formatNumber(Math.round(totalYear1 / 12)) || '-'}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-white">
                {targetType === 'rupiah' && totalYear2 ? 'Rp ' : ''}
                {formatNumber(Math.round(totalYear2 / 12)) || '-'}
              </td>
              <td className="px-3 py-3 text-center">
                <KinerjaColorBadge
                  pct={averageKinerja === null ? null : Number.parseFloat(averageKinerja.toFixed(2))}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Info panel */}
      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm font-semibold text-gray-700 dark:text-white mb-2">Informasi:</p>
        <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1 mb-3">
          <li>- Kenaikan persentase kinerja tiap tahun minimal 12% utk mengantisipasi lonjakan inflasi setiap tahun</li>
          <li>- Keterangan warna di rata-rata %kinerja:</li>
        </ul>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
              Hijau
            </span>
            <span className="text-xs text-gray-600 dark:text-gray-300">diatas 12%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">
              Kuning
            </span>
            <span className="text-xs text-gray-600 dark:text-gray-300">0 - 12%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
              Merah
            </span>
            <span className="text-xs text-gray-600 dark:text-gray-300">&lt;0%</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          - Apabila ingin di adjust bisa dilakukan di tabel bawah ini
        </p>
      </div>
    </div>
  );
}
